import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { assign, get, isPlainObject } from "lodash";
import { toast } from "vue-sonner";
import router from "@/router";

// 定义移动端特定配置
interface MobileAxiosConfig extends AxiosRequestConfig {
  // retryCount?: number; // 请求重试次数
  ignoreError?: boolean; // 是否忽略错误提示
}
// 生成UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string) => {
    const randomNum: number = (Math.random() * 16) | 0,
      value: number = c === 'x' ? randomNum : (randomNum & 0x3) | 0x8;
    return value.toString(16);
  });
}
class HttpService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({ timeout: 15000, withCredentials: true });

    // 请求拦截器
    this.instance.interceptors.request.use(
      this.handleRequest.bind(this),
      this.handleRequestError.bind(this)
    );
    // 响应拦截器
    this.instance.interceptors.response.use(
      this.handleResponse.bind(this),
      this.handleResponseError.bind(this)
    );
  }

  /**
   * 请求拦截处理
   */
  private handleRequest(
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig {
    // 添加AbortController
    const controller = new AbortController();
    config.signal = controller.signal;

    // 移动端特殊处理：添加网络状态检测
    if (this.getNetworkStatus() === "offline") {
      toast.error("网络连接不可用，请检查网络");
      throw new Error("网络连接不可用");
    }

    return config;
  }

  private getNetworkStatus(): "online" | "offline" {
    return navigator.onLine ? "online" : "offline";
  }

  /**
   * 响应拦截处理
   */
  private handleResponse(response: AxiosResponse) {
    const config = response.config as MobileAxiosConfig;

    // 移动端特殊处理：简化响应结构
    if (
      !config.url?.endsWith(".json") &&
      !(response.data instanceof Blob) &&
      !(response.data instanceof ArrayBuffer) &&
      isPlainObject(response.data)
    ) {
      const data = response.data;
      if (Array.isArray(data.data)) {
        return data.data;
      } else if (typeof data.data === "undefined" || data.data === null) {
        data.data = {};
      }
      if (data.message) {
        data.data.message = data.message;
      }
      if (data.code) {
        data.data.code = data.code;
      }
      return data.data;
    } else {
      return response.data;
    }
  }

  /**
   * 请求错误处理
   */
  private handleRequestError(error: AxiosError) {
    return Promise.reject(error);
  }

  /**
   * 响应错误处理
   */
  private async handleResponseError(error: AxiosError) {
    // 安全地获取config，确保它是有效的对象
    const config = (error.config as MobileAxiosConfig) || {};

    // // 重试逻辑
    // const retryCount = config.retryCount || 0;
    // if (error.code === "ECONNABORTED" && retryCount < 3) {
    //   return this.retryRequest(config, retryCount + 1);
    // }

    // 错误处理
    if (!config.ignoreError) {
      this.handleMobileError(error);
    }

    return Promise.reject(error);
  }

  /**
   * 移动端特定错误处理
   */
  private handleMobileError(error: AxiosError) {
    // 网络错误
    if (!error.response) {
      if (error.message.includes("timeout")) {
        toast.error("请求超时，请重试");
      } else {
        toast.error("网络连接失败");
      }
      return;
    }

    const { status, data } = error.response;

    // 根据状态码处理
    switch (status) {
      case 401:
        toast.error("登录已过期，请重新登录");
        // 清除本地存储的用户信息
        localStorage.removeItem("userInfo");
        // 触发登录状态变化事件
        window.dispatchEvent(new Event("login-status-changed"));
        // 重定向到登录页面
        router.replace({ name: "Login" });
        break;
      case 403:
        toast.error("没有访问权限");
        break;
      case 404:
        toast.error("请求资源不存在");
        break;
      case 500:
        toast.error("服务器错误，请稍后再试");
        break;
      default:
        // 显示后端返回的错误消息
        const message = get(data, "message") || "请求失败";
        toast.error(message);
    }
  }

  // /**
  //  * 重试请求
  //  */
  // private retryRequest(config: MobileAxiosConfig, retryCount: number) {
  //   return new Promise((resolve) => {
  //     setTimeout(() => {
  //       console.log(`请求重试 (${retryCount}): ${config.url}`);
  //       resolve(this.request({ ...config, retryCount }));
  //     }, 1000 * retryCount); // 指数退避
  //   });
  // }

  /**
   * 统一请求方法
   */
  public request<T>(config: MobileAxiosConfig): Promise<T> {
    if (
      config.url &&
      !config.url.startsWith("https://") &&
      !config.url.startsWith("http://") &&
      !config.url.startsWith("assets") &&
      !config.url.startsWith("/api")
    ) {
      // 获取环境变量
      config.baseURL = import.meta.env.VITE_API_BASE_URL || "/c4c3";
    }
    const xencio_trace_id: string = `${generateUUID()}_${new Date().getTime()}`; // uuid_timestamp
    config.headers = assign(config.headers || {}, {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "Sat, 01 Jan 2000 00:00:00 GMT",
      "If-Modified-Since": "0",
      'xencio-trace-id': xencio_trace_id
    });
    return this.instance(config) as Promise<T>;
  }

  /**
   * GET 请求
   */
  public get<T>(
    url: string,
    params?: unknown,
    config?: MobileAxiosConfig
  ): Promise<T> {
    return this.request<T>({ ...config, method: "GET", url, params });
  }

  /**
   * POST 请求
   */
  public post<T>(
    url: string,
    data?: unknown,
    config?: MobileAxiosConfig
  ): Promise<T> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  /**
   * PUT 请求
   */
  public put<T>(
    url: string,
    data?: unknown,
    config?: MobileAxiosConfig
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  /**
   * DELETE 请求
   */
  public delete<T>(
    url: string,
    params?: unknown,
    config?: MobileAxiosConfig
  ): Promise<T> {
    return this.request<T>({ ...config, method: "DELETE", url, params });
  }
}

// 创建服务实例（根据环境配置基础URL）
const httpService = new HttpService();

export default httpService;
